import { z } from 'zod';
import { OrgRoleSlugSchema, WorkspaceRoleSlugSchema } from '../identity/members.schemas';
import { PERMISSIONS } from '../permissions';

/**
 * WorkOS-issued user identity surfaced through `/auth/me` and `/auth/callback`.
 * Application-side concepts (workspace, membership) are sourced from
 * the identity module, not from WorkOS — see `OrganisationMembershipSchema`.
 */
export const AuthenticatedUserSchema = z.object({
  id: z.string().min(1),
  email: z.email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  // Whether the user has established a durable IdP login (SSO / password) vs
  // only the one-time invite MagicAuth flow (IMM-271). The member-onboarding
  // wizard shows a "connect your login" first step until this is true.
  idpConnected: z.boolean(),
});

export type AuthenticatedUser = z.infer<typeof AuthenticatedUserSchema>;

/**
 * One workspace membership entry on `/auth/me`. Despite the field name, each
 * element is the caller's standing in a workspace.
 *
 * `id` is the member's primary organisation in that workspace (their Haupt-
 * standort when unset falls back to the workspace main). `workspaceId` is the
 * workspace the member row belongs to — use it for `/workspaces/:id/...` routes.
 * Organisation-scoped permissions are fetched per Standort via
 * `GET /v1/me/organizations/:organizationId/permissions`.
 *
 * `onboardingStatus` (IMM-271) carries the member's onboarding lifecycle marker
 * for this workspace — `null` when the member has no onboarding record. The
 * client gate routes a member with a `pending` / `in_progress` status into the
 * onboarding flow and out of the app until it reads `completed` (or null).
 *
 * `companyOnboardingStatus` (IMM-322) carries the workspace's main-company
 * onboarding marker — `null` when the workspace has no company-onboarding record
 * (e.g. workspaces created before the gate existed). The client gate holds the
 * workspace behind company onboarding while this is `in_progress`: a member who
 * may update the company is routed into the company wizard, others see a blocking
 * "ask an organisation owner" screen until it reads `completed` (or null).
 */
export const OrganisationMembershipSchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().uuid(),
  onboardingStatus: z.enum(['pending', 'in_progress', 'completed']).nullable(),
  companyOnboardingStatus: z.enum(['in_progress', 'completed']).nullable(),
  // Whether the caller holds `workspace:identity:company-update` in THIS
  // workspace, i.e. could complete its company onboarding themselves. Lets the
  // client tell, for every workspace it can switch into, whether entering one
  // whose company onboarding is still `in_progress` would land the member on the
  // un-bypassable "ask an owner" blocking screen (they cannot) or in the wizard
  // (they can) — so the location selector can disable a dead-end switch (IMM-322).
  canOnboardCompany: z.boolean(),
  avatarMediaId: z.string().uuid().nullable(),
});

export type OrganisationMembership = z.infer<typeof OrganisationMembershipSchema>;

/**
 * A single Standort the caller is affiliated with, within a workspace group on
 * `/auth/me`. Carries just enough to render and switch in the location selector;
 * the full Standort detail (address, onboarding status) is still fetched per
 * workspace via `GET /v1/workspaces/:id/organizations`.
 */
export const WorkspaceGroupOrganisationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
});

export type WorkspaceGroupOrganisation = z.infer<typeof WorkspaceGroupOrganisationSchema>;

/**
 * The caller's standing in ONE workspace, with the Standorte they are affiliated
 * with nested under it (IMM-271 follow-up). Lets the client group the location
 * selector by workspace and switch the active workspace for a user who belongs
 * to several — a partner manager onboarded into a second workspace would
 * otherwise be pinned to their first membership and never reach the new Standort.
 * `organisations` mirrors `list-my-organizations` (affiliation-scoped), so an
 * Owner/Admin still resolves the full Standort list per workspace separately.
 */
export const WorkspaceGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  // The workspace's URL slug — `<slugified-name>-<immutable-uuid-suffix>`
  // (IMM-322). The web app routes on this (`/w/:slug`) and resolves it back to
  // `id` for API calls; it is recomputed from the name on rename, so links stay
  // valid via the immutable suffix. See `composeWorkspaceSlug` / `slugIdOf`.
  slug: z.string().min(1),
  // Non-production testing workspace — drives the app-wide yellow testing banner.
  isTest: z.boolean(),
  organisations: z.array(WorkspaceGroupOrganisationSchema),
  offeredRoles: z.object({
    workspaceRoles: z.array(WorkspaceRoleSlugSchema),
    organisationRoles: z.array(OrgRoleSlugSchema),
  }),
  // The caller's WORKSPACE-scope permissions in this workspace — what the route gate answers for a workspace-addressed route.
  permissions: z.array(z.enum(PERMISSIONS)),
});

export type WorkspaceGroup = z.infer<typeof WorkspaceGroupSchema>;

/**
 * Sign-in provider hint passed to `GET /auth/sign-in`. `authkit` defers
 * provider selection to WorkOS's hosted UI; `google` and `microsoft` short-
 * circuit straight to the configured OAuth provider.
 */
export const SignInProviderSchema = z.enum(['authkit', 'google', 'microsoft']);
export type SignInProvider = z.infer<typeof SignInProviderSchema>;

export const SignInQuerySchema = z.object({
  provider: SignInProviderSchema.default('authkit'),
  // Optional `login_hint` — pre-fills the e-mail on the IdP sign-in screen. Used
  // by the member-onboarding connect-your-login step (IMM-271).
  loginHint: z.email().optional(),
  // `prompt=login` — forces the IdP to re-authenticate instead of silently
  // reusing a live session. Query strings carry it as "true"/"false".
  forceLogin: z.stringbool().optional(),
  // Where to land after the IdP round-trip (IMM-486). A same-origin **app path**
  // only — must start with a single `/`. The server carries it opaquely inside
  // the WorkOS `state`; the client re-validates it (`safeReturnTo`) before it
  // ever navigates, so this schema only bounds its length.
  returnTo: z.string().max(2048).optional(),
});

export const SignInResponseSchema = z.object({
  url: z.url(),
});

export const CallbackBodySchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1).optional(),
});

export const CallbackResponseSchema = z.object({
  accessToken: z.string().min(1),
});

export const RefreshResponseSchema = z.object({
  accessToken: z.string().min(1),
});

export const SignOutResponseSchema = z.object({
  url: z.url(),
});

export const MeResponseSchema = z.object({
  user: AuthenticatedUserSchema,
  organisations: z.array(OrganisationMembershipSchema),
  // Workspace-grouped view of the same memberships, each carrying the caller's
  // affiliated Standorte. Drives the workspace-grouped location selector.
  workspaces: z.array(WorkspaceGroupSchema),
});

export type MeResponse = z.infer<typeof MeResponseSchema>;
